import { isEmpty } from 'ramda';
import i18n from 'helpers/i18n';
import messages from './messages';
import RouteHelpers from 'helpers/routes';
import useAsync from 'hooks/useAsync';
import Spacer from 'components/Spacer';
import { Section } from '../../Styled';
import { useLinkClickHandler } from 'react-router-dom';
import React, { useEffect } from 'react';
import { Button } from '@teachstonellc/component-library';
import { Calendar } from '@teachstonellc/component-library/dist/icons';
import { useTraining } from '../../TrainingContext';
import { getTrainingCourses } from 'actions/trainings';
import { getCourseAccess, getCourseDuration } from './utils';

import {
  Header,
  Item,
  ItemTitle,
  ItemSubtitle,
  ItemAction,
  ItemMessage
} from './Styled';

const CoursesSection: React.FC = () => {
  const { training, template } = useTraining();
  const { run, data, isSuccess } = useAsync();
  const certificationsPath = RouteHelpers.getPath('certifications');
  const onCertificationsClick =
    useLinkClickHandler<HTMLAnchorElement>(certificationsPath);

  useEffect(() => {
    run(getTrainingCourses(training.id));
  }, [run, training.id]);

  const coursesAvailable = isSuccess && !isEmpty(data.courses);
  const reliabilityAvailable = isSuccess && template.reliability_test;
  const knowledgeAvailable = isSuccess && template.knowledge_test;
  const combinedTestAvailable = isSuccess && training.combined_test_report;
  const testAvailable =
    combinedTestAvailable || reliabilityAvailable || knowledgeAvailable;

  if (!coursesAvailable && !testAvailable) {
    return null;
  }

  const testRows = [
    {
      key: 'combinedTest',
      title: messages.reliabilityKnowledge,
      enabled: combinedTestAvailable
    },
    {
      key: 'reliabilityTest',
      title: messages.reliability,
      enabled: reliabilityAvailable && !combinedTestAvailable
    },
    {
      key: 'knowledgeTest',
      title: messages.knowledge,
      enabled: knowledgeAvailable && !combinedTestAvailable
    }
  ].filter(row => row.enabled);

  return (
    <Section>
      <Header>{i18n.ft(messages.header)}</Header>

      <Spacer size={16} />
      <div>
        {testRows.map(test => (
          <Item key={test.key}>
            <div>
              <ItemTitle>{i18n.ft(test.title)}</ItemTitle>
              <ItemSubtitle>
                <Calendar className="mr-1 inline-block h-4 w-4" />
                {i18n.ft(messages.weeksOfAccess)}
              </ItemSubtitle>
            </div>
            <ItemAction>
              {training.participant_status === 'completed' ? (
                <Button
                  as="a"
                  href={certificationsPath}
                  onClick={onCertificationsClick}
                  fullWidth
                  shape="rounded"
                >
                  {i18n.ft(messages.goToCertifications)}
                </Button>
              ) : (
                <ItemMessage>{i18n.ft(messages.uponCompletion)}</ItemMessage>
              )}
            </ItemAction>
          </Item>
        ))}

        {data.courses.map((course: any) => {
          const access = getCourseAccess(course);
          const duration = getCourseDuration(course);

          return (
            <Item key={course.lms_course_id}>
              <div>
                <ItemTitle>{course.name}</ItemTitle>
                {duration && (
                  <ItemSubtitle>
                    <Calendar className="mr-1 inline-block h-4 w-4" />
                    {duration}
                  </ItemSubtitle>
                )}
              </div>
              <ItemAction>
                {access.enabled ? (
                  <Button
                    as="a"
                    href={
                      course.type === 'TotaraCourse'
                        ? '#'
                        : RouteHelpers.getPath('canvas-course', {
                            id: course.lms_course_id
                          })
                    }
                    fullWidth
                    shape="rounded"
                  >
                    {access.message}
                  </Button>
                ) : (
                  <ItemMessage>{access.message}</ItemMessage>
                )}
              </ItemAction>
            </Item>
          );
        })}
      </div>
    </Section>
  );
};

export default CoursesSection;
